import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ItineraryDay, ItineraryService } from '../../../../mocks/itinerary';
import { agencyInfo } from '../../../../mocks/dashboard';
import ServiceItem from './ServiceItem';
import AddServiceModal from './AddServiceModal';

const brand = agencyInfo.brandColor;

interface Props {
  day: ItineraryDay;
  onUpdate: (day: ItineraryDay) => void;
  onDelete: () => void;
}

export default function DayCard({ day, onUpdate, onDelete }: Props) {
  const [showAddService, setShowAddService] = useState(false);
  const [editService, setEditService] = useState<ItineraryService | null>(null);
  const [editLocation, setEditLocation] = useState(false);
  const [locationValue, setLocationValue] = useState(day.location);
  const [accommodationValue, setAccommodationValue] = useState(day.accommodation);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: day.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : 'auto',
  };

  const dayTotal = day.services.reduce((sum, s) => sum + s.sellPrice, 0);

  const handleAddService = (service: ItineraryService) => {
    if (editService) {
      onUpdate({ ...day, services: day.services.map((s) => (s.id === service.id ? service : s)) });
    } else {
      onUpdate({ ...day, services: [...day.services, service] });
    }
    setShowAddService(false);
    setEditService(null);
  };

  const handleDeleteService = (id: string) => {
    onUpdate({ ...day, services: day.services.filter((s) => s.id !== id) });
  };

  const handleSaveLocation = () => {
    onUpdate({ ...day, location: locationValue, accommodation: accommodationValue });
    setEditLocation(false);
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div ref={setNodeRef} style={style as React.CSSProperties} className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
      {/* Day Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-50" style={{ backgroundColor: `${brand}08` }}>
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/60 text-gray-400 cursor-grab active:cursor-grabbing flex-shrink-0"
        >
          <i className="ri-drag-move-line text-base" />
        </div>

        {/* Day Badge */}
        <div
          className="w-10 h-10 rounded-xl flex flex-col items-center justify-center text-white flex-shrink-0"
          style={{ backgroundColor: brand }}
        >
          <span className="text-xs font-bold leading-none">Day</span>
          <span className="text-sm font-black leading-none">{day.dayNumber}</span>
        </div>

        <div className="flex-1 min-w-0">
          {editLocation ? (
            <div className="space-y-1.5">
              <input
                autoFocus
                value={locationValue}
                onChange={(e) => setLocationValue(e.target.value)}
                className="w-full text-sm font-semibold text-gray-800 bg-white/80 border border-gray-200 rounded-lg px-2 py-1 focus:outline-none"
                placeholder="Location..."
              />
              <input
                value={accommodationValue}
                onChange={(e) => setAccommodationValue(e.target.value)}
                className="w-full text-xs text-gray-500 bg-white/80 border border-gray-200 rounded-lg px-2 py-1 focus:outline-none"
                placeholder="Accommodation..."
              />
              <div className="flex gap-2">
                <button onClick={handleSaveLocation} className="text-xs px-2 py-0.5 rounded text-white cursor-pointer" style={{ backgroundColor: brand }}>Save</button>
                <button onClick={() => setEditLocation(false)} className="text-xs px-2 py-0.5 rounded border border-gray-200 text-gray-500 cursor-pointer">Cancel</button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-1">
                <p className="text-sm font-semibold text-gray-800 truncate">{day.location || `Day ${day.dayNumber} Location`}</p>
                <button onClick={() => setEditLocation(true)} className="w-5 h-5 flex items-center justify-center rounded text-gray-300 hover:text-gray-500 cursor-pointer">
                  <i className="ri-edit-line text-xs" />
                </button>
              </div>
              <div className="flex items-center gap-3 mt-0.5">
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <i className="ri-calendar-line" />
                  {formatDate(day.date)}
                </span>
                {day.accommodation && (
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <i className="ri-hotel-line" />
                    {day.accommodation}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {dayTotal > 0 && (
            <span className="text-xs font-semibold px-2 py-1 rounded-lg" style={{ backgroundColor: `${brand}15`, color: brand }}>
              AED {dayTotal.toLocaleString()}
            </span>
          )}
          <span className="text-xs text-gray-400">{day.services.length} services</span>
          <button
            onClick={onDelete}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-400 cursor-pointer"
          >
            <i className="ri-delete-bin-line text-sm" />
          </button>
        </div>
      </div>

      {/* Services */}
      <div className="p-4 space-y-2">
        {day.services.length === 0 ? (
          <div className="text-center py-6 text-gray-300 border-2 border-dashed border-gray-100 rounded-xl">
            <i className="ri-add-circle-line text-3xl mb-1 block" />
            <p className="text-sm">No services yet. Click below to add.</p>
          </div>
        ) : (
          day.services.map((service) => (
            <ServiceItem
              key={service.id}
              service={service}
              onEdit={() => { setEditService(service); setShowAddService(true); }}
              onDelete={() => handleDeleteService(service.id)}
            />
          ))
        )}

        <button
          onClick={() => { setEditService(null); setShowAddService(true); }}
          className="w-full mt-2 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-gray-200 text-sm text-gray-400 hover:border-gray-300 hover:text-gray-500 transition-colors cursor-pointer"
        >
          <i className="ri-add-line" />
          Add Service
        </button>
      </div>

      {showAddService && (
        <AddServiceModal
          dayNumber={day.dayNumber}
          editService={editService}
          onSave={handleAddService}
          onClose={() => { setShowAddService(false); setEditService(null); }}
        />
      )}
    </div>
  );
}
